import './assets/main.css'
import './styles/breakpoints.css'
import 'primeicons/primeicons.css'

// Validate env before any plugin initializes — fails loudly on missing config.
import './config/env'

import { createApp } from 'vue'
import { createPinia } from 'pinia'
import PrimeVue from 'primevue/config'
import HudStorePreset from './theme/primevue-preset'
import Tooltip from 'primevue/tooltip'
import ToastService from 'primevue/toastservice'
import ConfirmationService from 'primevue/confirmationservice'
import { VueQueryPlugin, QueryClient } from '@tanstack/vue-query'

import App from './App.vue'
import router from './router'
import i18n, { initializeLocale } from './i18n'
import { useAuthStore } from './stores/auth'
import { vPermission } from './directives/vPermission'
import { vStackLabels } from './directives/vStackLabels'
import { initSentry } from './sentry'

const app = createApp(App)

// Initialize Sentry error tracking (before other plugins)
initSentry(app, router)

app.use(createPinia())

// Initialize auth store after Pinia is set up
const authStore = useAuthStore()
authStore.initAuth()

// Initialize locale based on authentication state
initializeLocale()

app.use(router)
app.use(i18n)
// Configure TanStack Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      // Exponential backoff, capped at 30s. Without this, two retries of a
      // flapping endpoint can hit the network back-to-back in <100ms and
      // both fail for the same transient reason. Cap stops the wait time
      // from growing past 30s on later retries (we already retry max 2).
      retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 30_000),
      staleTime: 5 * 60 * 1000, // 5 minutes
      // Global default: false (avoid surprise refetch on tab focus for
      // non-critical lists). Critical queries (deliveries-in-progress,
      // payments-pending) should opt-in with `refetchOnWindowFocus: 'always'`
      // at the useQuery call site.
      refetchOnWindowFocus: false,
    },
  },
})

app.use(VueQueryPlugin, {
  queryClient,
})
app.use(PrimeVue, {
  theme: {
    preset: HudStorePreset,
    options: {
      darkModeSelector: '.app-dark',
    },
  },
  // autoZIndex assigns each newly-opened overlay a value above the current stack,
  // so a dropdown opened inside a Dialog always lands above the dialog mask. The
  // bases below only need to clear the app topbar (--app-z-topbar: 1002); the
  // dynamic increment handles modal stacking. Keep these as the single source of
  // truth — never re-add static z-index !important rules for these overlays.
  zIndex: {
    modal: 1100, // Dialog / Drawer masks
    overlay: 1100, // Select / Dropdown / MultiSelect / DatePicker panels
    menu: 1100, // Menu / ContextMenu / TieredMenu
    tooltip: 1200, // Tooltip / always-on-top
  },
})

// Register PrimeVue services and directives
app.use(ToastService)
app.use(ConfirmationService)
app.directive('tooltip', Tooltip)
app.directive('permission', vPermission)
app.directive('stack-labels', vStackLabels)

// v-autofocus: focus the first focusable input on mount, so create forms start
// ready for typing (cuts a tap/click at the key entry step). Works on PrimeVue
// components (root is the input) and plain wrappers alike. Pass a falsy binding
// value to skip (e.g. v-autofocus="!isEdit").
app.directive('autofocus', {
  mounted(el: HTMLElement, binding) {
    if (binding.value === false) return
    const input = (
      el.matches('input, textarea, select') ? el : el.querySelector('input, textarea, select')
    ) as HTMLElement | null
    if (input) requestAnimationFrame(() => input.focus())
  },
})

app.mount('#app')
